import { memo, type ReactElement } from 'react';
import { MaterialIcon } from '@lapo/ui';
import type { TodoistTaskDTO, TodoistTodayDTO } from './auth/apiClient.js';

/**
 * The Today list — the body of the right-hand drawer. Mirrors Todoist's Today view: an Overdue group
 * above a Today group, tasks in the same order (day_order) and colors (priority flag, label chips,
 * project dot) the Todoist app shows. Checking a task completes it in Todoist. Purely presentational;
 * loading/refresh/completion and the drawer open state live in App.
 */
export interface TodoistPanelProps {
  /** Whether a Todoist account is linked. When false we show a connect hint instead of the list. */
  connected: boolean;
  loading: boolean;
  error: string | null;
  data: TodoistTodayDTO | null;
  /** The stored Todoist token is revoked/invalid — show a reconnect prompt instead of the list. */
  needsReconnect?: boolean;
  /** Task ids whose server completion is in flight (their checkbox shows a spinner). */
  completing: ReadonlySet<string>;
  /** The task struck out and awaiting the undo window (not yet sent to the server). */
  pendingTaskId?: string;
  /** Content of the pending task, shown in the undo bar; null when no undo is active. */
  undoContent?: string | null;
  onRefresh: () => void;
  onComplete: (task: TodoistTaskDTO) => void;
  /** Cancel the pending completion (un-strike, no server call). */
  onUndo: () => void;
  /** Dismiss the undo bar and commit the completion immediately. */
  onDismissUndo: () => void;
  /** Open Settings so the user can link Todoist (shown from the connect hint). */
  onConnect: () => void;
  /** Re-run the Todoist OAuth link to restore a revoked token (shown from the reconnect prompt). */
  onReconnect?: () => void;
}

/** "3:00 PM" for a timed due, "" otherwise. */
function dueTime(task: TodoistTaskDTO): string {
  if (!task.due?.hasTime || !task.due.datetime) return '';
  const d = new Date(task.due.datetime);
  if (Number.isNaN(d.getTime())) return '';
  return d.toLocaleTimeString(undefined, { hour: 'numeric', minute: '2-digit' });
}

/** "Jun 28" for an overdue task's date, so the user sees how stale it is (Todoist does the same). */
function overdueDate(task: TodoistTaskDTO): string {
  if (!task.due?.date) return '';
  // Parse as local midnight so the label doesn't shift a day across timezones.
  const [y, m, d] = task.due.date.slice(0, 10).split('-').map(Number);
  if (!y || !m || !d) return '';
  return new Date(y, m - 1, d).toLocaleDateString(undefined, { month: 'short', day: 'numeric' });
}

function TaskRow({
  task,
  completing,
  pending,
  onComplete,
}: {
  task: TodoistTaskDTO;
  completing: boolean;
  /** Struck out and awaiting the undo window — shown as done, checkbox filled, clicks ignored. */
  pending: boolean;
  onComplete: (task: TodoistTaskDTO) => void;
}): ReactElement {
  const ring = task.priorityColor ?? 'var(--lapo-faint)';
  const time = dueTime(task);
  return (
    <div className="lapo-td-row" data-completing={completing ? 'true' : 'false'} data-pending={pending ? 'true' : 'false'}>
      <button
        type="button"
        className="lapo-td-check"
        style={{ borderColor: ring, color: ring }}
        title="Complete"
        aria-label={`Complete ${task.content}`}
        disabled={completing || pending}
        onClick={() => onComplete(task)}
      >
        {completing ? (
          <MaterialIcon name="progress_activity" size={14} className="lapo-td-spin" />
        ) : (
          <MaterialIcon name="check" size={14} className="lapo-td-check-mark" />
        )}
      </button>
      <div className="lapo-td-body">
        <div className="lapo-td-content">{task.content}</div>
        {(task.labels.length > 0 || task.project || task.overdue || time || task.due?.recurring) && (
          <div className="lapo-td-meta">
            {task.overdue && overdueDate(task) && <span className="lapo-td-overdue">{overdueDate(task)}</span>}
            {time && <span className={task.overdue ? 'lapo-td-overdue' : 'lapo-td-time'}>{time}</span>}
            {task.due?.recurring && <MaterialIcon name="autorenew" size={12} className="lapo-td-recur" />}
            {task.labels.map((l) => (
              <span key={l.name} className="lapo-td-label" style={{ color: l.color, borderColor: l.color }}>
                {l.name}
              </span>
            ))}
            {task.project && (
              <span className="lapo-td-project">
                <span className="lapo-td-dot" style={{ background: task.project.color }} />
                {task.project.name}
              </span>
            )}
          </div>
        )}
      </div>
    </div>
  );
}

// Memoized (audit F3) — see AgendaPanel. Hoisted so the export wraps it above the definition.
export const TodoistPanel = memo(TodoistPanelBase);

function TodoistPanelBase({
  connected,
  loading,
  error,
  data,
  needsReconnect,
  completing,
  pendingTaskId,
  undoContent,
  onRefresh,
  onComplete,
  onUndo,
  onDismissUndo,
  onConnect,
  onReconnect,
}: TodoistPanelProps): ReactElement {
  const empty =
    connected && !loading && !error && !needsReconnect && data && data.overdue.length === 0 && data.today.length === 0;
  return (
    <div className="lapo-td">
      <div className="lapo-td-head">
        <span className="lapo-td-title">Today</span>
        {connected && (
          <button
            type="button"
            className="lapo-icon-btn"
            title={loading ? 'Refreshing…' : 'Refresh'}
            aria-label={loading ? 'Refreshing' : 'Refresh'}
            disabled={loading}
            onClick={onRefresh}
          >
            {/* Spin while a background refresh is in flight (the hydrate-then-refresh indicator). */}
            <MaterialIcon name={loading ? 'progress_activity' : 'refresh'} size={18} className={loading ? 'lapo-td-spin' : undefined} />
          </button>
        )}
      </div>
      <div className="lapo-td-scroll">
        {!connected && (
          <div className="lapo-td-note">
            Connect Todoist to see your Today list.
            <br />
            <button type="button" className="lapo-td-link" onClick={onConnect}>
              Connect Todoist
            </button>
          </div>
        )}
        {connected && needsReconnect && (
          <div className="lapo-td-note lapo-td-error">
            Todoist needs reconnecting — your connection expired.
            <br />
            <button type="button" className="lapo-td-link" onClick={onReconnect}>
              Reconnect Todoist
            </button>
          </div>
        )}
        {connected && !needsReconnect && loading && !data && <div className="lapo-td-note">Loading…</div>}
        {connected && !needsReconnect && error && <div className="lapo-td-note lapo-td-error">{error}</div>}
        {empty && <div className="lapo-td-note">Nothing due today. 🎉</div>}
        {connected && !needsReconnect && data && data.overdue.length > 0 && (
          <section className="lapo-td-group">
            <h3 className="lapo-td-group-title lapo-td-group-overdue">
              Overdue <span className="lapo-td-count">{data.overdue.length}</span>
            </h3>
            {data.overdue.map((t) => (
              <TaskRow
                key={t.taskId}
                task={t}
                completing={completing.has(t.taskId)}
                pending={t.taskId === pendingTaskId}
                onComplete={onComplete}
              />
            ))}
          </section>
        )}
        {connected && !needsReconnect && data && data.today.length > 0 && (
          <section className="lapo-td-group">
            {data.overdue.length > 0 && (
              <h3 className="lapo-td-group-title">
                Today <span className="lapo-td-count">{data.today.length}</span>
              </h3>
            )}
            {data.today.map((t) => (
              <TaskRow
                key={t.taskId}
                task={t}
                completing={completing.has(t.taskId)}
                pending={t.taskId === pendingTaskId}
                onComplete={onComplete}
              />
            ))}
          </section>
        )}
      </div>
      {undoContent != null && (
        <div className="lapo-td-undo" data-testid="td-undo">
          <span className="lapo-td-undo-text">
            Completed “{undoContent}”
          </span>
          <button type="button" className="lapo-td-undo-btn" onClick={onUndo}>
            Undo
          </button>
          <button type="button" className="lapo-td-undo-x" aria-label="Dismiss" title="Dismiss" onClick={onDismissUndo}>
            <MaterialIcon name="close" size={14} />
          </button>
        </div>
      )}
    </div>
  );
}
